import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../context/AuthContext";
import { listCompanyUsers, getCalendar, getTimesheet } from "../api";
import { statusesAsList, countWorkDays } from "../constants";
import { computeStats } from "../controllers/salary";
import { todayParts, formatDate } from "../utils/dates";
import OnboardingChecklist from "../components/OnboardingChecklist";
import TaxBurdenWidget from "../components/TaxBurdenWidget";
import EcpCompanyBanner from "../components/EcpCompanyBanner";
import AppPage from "../components/AppPage";

const MONTH_KEYS = ["jan","feb","mar","apr","may","jun","jul","aug","sep","oct","nov","dec"];

export default function Dashboard() {
  const { t } = useTranslation();
  const { user, company } = useAuth();
  const [users, setUsers] = useState([]);
  const [calendar, setCalendar] = useState({ days: {} });
  const [timesheet, setTimesheet] = useState({ entries: {} });
  const { year, month } = todayParts();
  const monthName = t(`months.${MONTH_KEYS[month]}`);

  useEffect(() => {
    (async () => {
      const [u, cal, ts] = await Promise.all([
        listCompanyUsers(),
        getCalendar(year, month),
        getTimesheet(year, month),
      ]);
      setUsers(u);
      setCalendar(cal);
      setTimesheet(ts);
    })();
  }, [year, month]);

  const norm = useMemo(() => countWorkDays(calendar.days), [calendar]);
  const statuses = useMemo(() => statusesAsList(company), [company]);
  const myStats = useMemo(() => user ? computeStats(timesheet.entries, user.user_id, calendar.days, statuses) : null,
                           [timesheet, calendar, user, statuses]);

  const todayDay = String(new Date().getDate()).padStart(2, "0");
  const todayMarked = users.filter(u => timesheet.entries?.[`${u.user_id}_${todayDay}`]).length;

  return (
    <AppPage
      testId="dashboard"
      bodyScroll="scroll"
      toolbar={(
        <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
          <div>
            <h4 className="fw-bold mb-1">{t("dashboard.welcome", { name: user?.name })}</h4>
            <div className="text-muted small">
              {t(`roles.${user?.role}`)} · {company?.name} · {t("dashboard.today_is", { date: formatDate(new Date()) })}
            </div>
          </div>
        </div>
      )}
    >
      <EcpCompanyBanner />

      <OnboardingChecklist user={user} company={company} employeesCount={users.length} />

      <TaxBurdenWidget user={user} company={company} />

      <div className="row g-3 mb-4">
        <div className="col-6 col-md-3"><div className="kpi-card kpi-1">
          <div className="small opacity-75">{t("dashboard.employees_count")}</div>
          <div className="fs-3 fw-bold" data-testid="kpi-employees">{users.length}</div>
        </div></div>
        <div className="col-6 col-md-3"><div className="kpi-card kpi-2">
          <div className="small opacity-75">{t("dashboard.norm_days_label", { month: monthName })}</div>
          <div className="fs-3 fw-bold" data-testid="kpi-norm">{norm}</div>
        </div></div>
        <div className="col-6 col-md-3"><div className="kpi-card kpi-3">
          <div className="small opacity-75">{t("dashboard.marked_today")}</div>
          <div className="fs-3 fw-bold" data-testid="kpi-today-marked">{todayMarked} / {users.length}</div>
        </div></div>
        <div className="col-6 col-md-3"><div className="kpi-card kpi-4">
          <div className="small opacity-75">{t("dashboard.my_worked")}</div>
          <div className="fs-3 fw-bold" data-testid="kpi-my-worked">{myStats?.worked ?? 0}</div>
        </div></div>
      </div>

      <div className="row g-3">
        <div className="col-lg-7">
          <div className="card card-soft"><div className="card-body">
            <h6 className="fw-semibold mb-3">{t("dashboard.status_summary", { month: monthName, year })}</h6>
            <div className="d-flex flex-wrap gap-3">
              {statuses.map(s => {
                const count = Object.values(timesheet.entries || {}).filter(e => e.status === s.key).length;
                return (
                  <div key={s.key} className="d-flex align-items-center gap-2">
                    <span className="status-pill" style={{ background: s.color }}>{s.short}</span>
                    <div>
                      <div className="small fw-semibold">{s.label}</div>
                      <div className="text-muted small">{t("dashboard.marks_count", { count })}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div></div>
        </div>
        <div className="col-lg-5">
          <div className="card card-soft"><div className="card-body">
            <h6 className="fw-semibold mb-3">{t("dashboard.my_timesheet", { month: monthName })}</h6>
            {myStats && (
              <ul className="list-unstyled mb-0 small">
                <li className="d-flex justify-content-between border-bottom py-1"><span>{t("dashboard.worked")}</span><span className="fw-semibold">{myStats.worked}</span></li>
                <li className="d-flex justify-content-between border-bottom py-1"><span>{t("dashboard.halfday")}</span><span className="fw-semibold">{myStats.halfday}</span></li>
                <li className="d-flex justify-content-between border-bottom py-1"><span>{t("dashboard.sick")}</span><span className="fw-semibold">{myStats.sick}</span></li>
                <li className="d-flex justify-content-between border-bottom py-1"><span>{t("dashboard.vacation")}</span><span className="fw-semibold">{myStats.vacation}</span></li>
                <li className="d-flex justify-content-between border-bottom py-1"><span>{t("dashboard.absent")}</span><span className="fw-semibold text-danger">{myStats.absent}</span></li>
                <li className="d-flex justify-content-between py-1"><span>{t("dashboard.norm")}</span><span className="fw-semibold">{norm}</span></li>
              </ul>
            )}
          </div></div>
        </div>
      </div>
    </AppPage>
  );
}
